import { FC, useState } from 'react';
import { Formik } from 'formik';
import { Box, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Form } from 'react-router-dom';

import styles from './SetReminderModal.module.scss';
import { useSetReminderMutation } from '../../../../../store/services/tripsApi';
import ArrivalTimePicker from '../../../../../common/ArrivalTimePicker';
import CustomButton from '../../../../../common/CustomButton';
import { MODAL_ABOVE_FULL_SCREEN_Z, PORTAL_POPOVER_Z } from '../../../../../constants/modalZIndex';

type FormikValues = {
  arrivalTime: string;
};

interface SetReminderModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialTime: string;
  tripStopId: string;
  selectedDate: string;
  apiPayload: {
    arrival_date_time: string;
    departure_date_time: string;
    location_from_address: {
      name: string;
      latitude: number;
      longitude: number;
    };
  };
}

const SetReminderModal: FC<SetReminderModalProps> = ({
  open,
  onClose,
  onSuccess,
  initialTime,
  tripStopId,
  selectedDate,
  apiPayload,
}) => {
  const [resErrors, setResErrors] = useState<string | null>(null);
  const [setReminder, { isLoading }] = useSetReminderMutation();

  const initialValues: FormikValues = {
    arrivalTime: initialTime || '',
  };

  const parseBackendError = (error: any): string => {
    if (error?.data?.type === 'validation_error' && error?.data?.errors) {
      const errorMessages = error.data.errors
        .map((err: any) => err.detail)
        .filter((detail: string) => detail && detail.trim() !== '');

      if (errorMessages.length > 0) {
        if (errorMessages.length > 1) {
          return errorMessages.map((msg: string, index: number) => `${index + 1}. ${msg}`).join('\n');
        } else {
          return errorMessages[0];
        }
      }
    }
    if (error?.data?.message) return error.data.message;
    if (error?.data?.detail) return error.data.detail;
    if (error?.message) return error.message;

    return 'Failed to set reminder. Please try again.';
  };

  const handleSubmit = async (values: FormikValues) => {
    try {
      setResErrors(null);

      const fullDateTime = selectedDate && values.arrivalTime ? `${selectedDate}T${values.arrivalTime}:00` : '';

      if (!fullDateTime) {
        setResErrors('Invalid date or time format');
        return;
      }
      const updatedPayload = {
        ...apiPayload,
        arrival_date_time: new Date(fullDateTime).toISOString(),
      };

      console.log('Setting reminder with payload:', updatedPayload);

      await setReminder({
        tripStopId,
        payload: updatedPayload,
      }).unwrap();

      console.log('Reminder set successfully');
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Failed to set reminder:', error);
      const errorMessage = parseBackendError(error);
      setResErrors(errorMessage);
    }
  };

  const formikValidate = (values: FormikValues) => {
    const errors: Partial<FormikValues> = {};

    if (!values.arrivalTime) {
      errors.arrivalTime = 'Arrival time is required';
    }

    if (values.arrivalTime && !/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(values.arrivalTime)) {
      errors.arrivalTime = 'Invalid time format';
    }

    return errors;
  };

  const handleClose = () => {
    onClose();
    setResErrors(null);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="set-reminder-modal"
      sx={{
        zIndex: MODAL_ABOVE_FULL_SCREEN_Z,
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(34, 43, 58, 0.2)',
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          maxWidth: '400px',
          minWidth: 320,
          width: '100%',
          bgcolor: 'background.paper',
          borderRadius: '20px',
          p: '32px',
          overflow: 'visible',
          overflowY: 'auto',
          boxShadow: 'none',
          maxHeight: '90vh',
          '@media (max-width: 600px)': {
            p: '24px',
            minWidth: 280,
          },
        }}
      >
        <Box sx={{ position: 'absolute', top: 16, right: 20, cursor: 'pointer' }}>
          <CloseIcon
            sx={{ width: '30px', height: '30px' }}
            onClick={handleClose}
          />
        </Box>

        <Box className={styles.modalContent}>
          <Typography
            variant="h5Bold"
            sx={{ textAlign: 'center', mb: 3 }}
            color="#222B3A"
          >
            Set Reminder
          </Typography>

          <Formik
            initialValues={initialValues}
            validate={formikValidate}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ values, handleChange, handleSubmit, errors, touched }) => (
              <Form
                className={styles.reminderForm}
                onSubmit={handleSubmit}
              >
                <Box className={styles.formField}>
                  <ArrivalTimePicker
                    inputLabel="When would you like to arrive?"
                    value={values.arrivalTime}
                    onChange={handleChange}
                    placeholder="Select time"
                    name="arrivalTime"
                    dropdownZIndex={PORTAL_POPOVER_Z}
                    error={Boolean(touched.arrivalTime && errors.arrivalTime ? errors.arrivalTime : '')}
                  />
                </Box>
                <Box className={styles.modalActions}>
                  <CustomButton
                    btnType="button"
                    variant="outlined"
                    onClick={handleClose}
                    disabled={isLoading}
                    customClass={styles.cancelButton}
                  >
                    Cancel
                  </CustomButton>
                  <CustomButton
                    btnType="submit"
                    variant="contained"
                    disabled={isLoading || !values.arrivalTime}
                    customClass={styles.submitButton}
                  >
                    {isLoading ? 'Setting Reminder...' : 'Set Reminder'}
                  </CustomButton>
                </Box>
                {resErrors && (
                  <Typography
                    variant="h12"
                    sx={{
                      color: '#C8102E',
                      textAlign: 'center',
                      mt: 2,
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {resErrors}
                  </Typography>
                )}
              </Form>
            )}
          </Formik>
        </Box>
      </Box>
    </Modal>
  );
};

export default SetReminderModal;
